<template>
  <VApp>
    <VMain>
      <div class="d-flex align-center justify-center fill-height pa-4">
        <VCard
          max-width="560"
          class="pa-6"
          data-testid="app-database-error-panel"
        >
          <h3 class="mb-4">{{ t(`${cause}.title`) }}</h3>
          <p class="mb-4">{{ t(`${cause}.description`) }}</p>
          <p class="mb-4">{{ t("clearCacheDescription") }}</p>
          <SocioAlert
            v-if="hasPendingChanges"
            type="error"
            density="comfortable"
            class="mb-4"
          >
            {{ t("pendingChanges") }}
          </SocioAlert>
          <SocioAlert
            v-if="!isOnline"
            type="warning"
            density="comfortable"
            class="mb-4"
          >
            {{ t("offline") }}
          </SocioAlert>
          <SocioAlert
            v-if="hasClearFailed"
            type="error"
            density="comfortable"
            class="mb-4"
          >
            {{ t("clearFailed") }}
          </SocioAlert>
          <template v-if="isConfirmingClear">
            <p class="mb-4">{{ t("confirmClear") }}</p>
            <div class="d-flex justify-end ga-2">
              <SocioBtn
                variant="text"
                :text="t('cancel')"
                :disabled="isRefreshing"
                data-testid="app-database-error-clear-cancel-button"
                @click="isConfirmingClear = false"
              />
              <SocioBtn
                :text="t('clearCache')"
                :loading="isRefreshing"
                :disabled="!isOnline"
                data-testid="app-database-error-clear-confirm-button"
                @click="clearCache"
              />
            </div>
          </template>
          <div v-else class="d-flex justify-end ga-2">
            <SocioBtn
              variant="text"
              :text="t('clearCache')"
              :disabled="!isOnline || isRetrying"
              data-testid="app-database-error-clear-cache-button"
              @click="isConfirmingClear = true"
            />
            <SocioBtn
              :loading="isRetrying"
              :disabled="isRefreshing"
              data-testid="app-database-error-retry-button"
              @click="retry"
            >
              <VIcon start>mdi-refresh</VIcon>
              {{ t("retry") }}
            </SocioBtn>
          </div>
        </VCard>
      </div>
    </VMain>
  </VApp>
</template>

<script setup>
  import { computed, onMounted, onUnmounted, ref } from "vue";
  import useSafeI18n from "@/composables/useSafeI18n";
  import { useAppRefresh } from "@/composables/useAppRefresh";
  import { activateWaitingWorker } from "@/utils/staleBuild";

  defineOptions({ name: "DatabaseOpenError" });

  const props = defineProps({
    isNewerVersion: {
      type: Boolean,
      required: true,
    },
    hasPendingChanges: {
      type: Boolean,
      required: true,
    },
  });

  const { t } = useSafeI18n();
  const { isRefreshing, performFullRefresh } = useAppRefresh();

  const cause = computed(() =>
    props.isNewerVersion ? "newerVersion" : "unreadable"
  );
  const isOnline = ref(navigator.onLine);
  const isRetrying = ref(false);
  const isConfirmingClear = ref(false);
  const hasClearFailed = ref(false);

  const updateOnline = () => {
    isOnline.value = navigator.onLine;
  };

  onMounted(() => {
    globalThis.addEventListener("online", updateOnline);
    globalThis.addEventListener("offline", updateOnline);
  });

  onUnmounted(() => {
    globalThis.removeEventListener("online", updateOnline);
    globalThis.removeEventListener("offline", updateOnline);
  });

  const retry = async () => {
    isRetrying.value = true;
    try {
      await activateWaitingWorker();
    } finally {
      globalThis.location.reload();
    }
  };

  const clearCache = async () => {
    hasClearFailed.value = false;
    try {
      await performFullRefresh({ syncPending: false });
    } catch {
      hasClearFailed.value = true;
    }
  };
</script>

<i18n>
{
  "fr": {
    "newerVersion": {
      "title": "Données locales plus récentes que l'application",
      "description": "Cet appareil a déjà été utilisé avec une version plus récente d'ATMO : la version chargée ne peut pas lire ses données locales. Réessayez dans quelques minutes, une version corrective est peut-être en cours de publication."
    },
    "unreadable": {
      "title": "Données locales illisibles",
      "description": "ATMO n'a pas pu ouvrir les données enregistrées sur cet appareil. Réessayez : l'erreur peut être passagère."
    },
    "clearCacheDescription": "Si le problème persiste, videz le cache : les données de l'appareil seront supprimées puis de nouveau chargées depuis le serveur.",
    "pendingChanges": "Des modifications enregistrées sur l'appareil n'ont pas encore été envoyées. Elles seront perdues si vous videz le cache.",
    "offline": "Connectez-vous au réseau pour vider le cache.",
    "clearFailed": "Le cache n'a pas pu être vidé. Réessayez une fois la connexion rétablie.",
    "confirmClear": "Confirmez la suppression des données enregistrées sur l'appareil.",
    "cancel": "Annuler",
    "retry": "Réessayer",
    "clearCache": "Vider le cache"
  },
  "en": {
    "newerVersion": {
      "title": "Local data is newer than the application",
      "description": "This device has already been used with a newer version of ATMO: the version loaded cannot read its local data. Try again in a few minutes, a corrected version may be on its way."
    },
    "unreadable": {
      "title": "Local data cannot be read",
      "description": "ATMO could not open the data saved on this device. Try again: the error may be temporary."
    },
    "clearCacheDescription": "If the problem persists, clear the cache: the device's data will be deleted, then loaded again from the server.",
    "pendingChanges": "Changes saved on this device have not been sent yet. They will be lost if you clear the cache.",
    "offline": "Connect to the network to clear the cache.",
    "clearFailed": "The cache could not be cleared. Try again once the connection is back.",
    "confirmClear": "Confirm the deletion of the data saved on this device.",
    "cancel": "Cancel",
    "retry": "Try again",
    "clearCache": "Clear cache"
  }
}
</i18n>
